/**
 * The camera handshake between the BIM view and the other two views (production's widget `poseFromView` /
 * `poseToView`, with the map's Web Mercator zoom in place of an Esri map scale).
 *
 * Without this, BIM is a second application sharing a screen. The shared camera's centre, zoom, bearing and pitch
 * become a Pose and the viewer opens exactly there; leaving BIM, the pose goes back the same way.
 *
 * - Zoom <-> distance: the ground the 2D map shows top to bottom at the centre, framed by the BIM camera's 50 degree
 *   vertical field of view (production's scaleToDistance, with the scale already true at this latitude).
 * - Bearing <-> heading: the BIM heading turns the other way to a compass bearing. Its frame is east, up, south, and
 *   the camera sits on the heading's side of the target looking back at it, so heading +90 degrees looks west.
 * - Pitch: the shared camera measures from straight down, BIM from the horizon. A 2D map that is not tilted has no
 *   camera angle to give, so arriving from it lands at production's oblique default rather than flat: arriving flat
 *   shows a plan view of a mesh, which reads as a broken 3D view.
 */
import type { Pose } from '@mighty-map/map-engine/bim';
import type { SharedCamera, ViewMode } from '../../state/viewMode';

/** The BIM engine's camera pose (metres and radians, pitch above the horizon), which this module converts. */
export type { Pose };

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

/** WGS84 equator, metres: the Web Mercator world width the map's zoom levels divide. */
export const EARTH_CIRCUMFERENCE = 40075016.686;
/** The 2D map's tiles are 512 px, so the whole world is 512 px across at zoom 0. */
export const MAP_TILE_PX = 512;
/** The BIM camera's vertical field of view (the viewer's PerspectiveCamera). */
export const BIM_FOV_DEG = 50;
/** Production's arrival pitch from a 2D map, radians above the horizon (about 35 degrees). */
export const MAP_ARRIVAL_PITCH = 0.62;
/** Below this many degrees of tilt, a 2D map counts as flat (it has no camera angle of its own). */
const FLAT_DEG = 1;

/** Ground metres per CSS pixel at a latitude, for the 2D map's zoom. */
export function metresPerPixel(zoom: number, lat: number): number {
  return (EARTH_CIRCUMFERENCE * Math.cos(lat * D2R)) / (MAP_TILE_PX * 2 ** zoom);
}

/** The camera distance that frames what the map shows at `zoom`, over a view `screenPx` tall. */
export function zoomToDistance(zoom: number, lat: number, screenPx: number, fovDeg = BIM_FOV_DEG): number {
  const ground = metresPerPixel(zoom, lat) * screenPx; // metres visible top to bottom
  return ground / (2 * Math.tan((fovDeg * D2R) / 2));
}

/** The map zoom that shows what the camera frames from `dist`, over a view `screenPx` tall. */
export function distanceToZoom(dist: number, lat: number, screenPx: number, fovDeg = BIM_FOV_DEG): number {
  const mpp = (dist * 2 * Math.tan((fovDeg * D2R) / 2)) / (screenPx || 1);
  return Math.log2((EARTH_CIRCUMFERENCE * Math.cos(lat * D2R)) / (MAP_TILE_PX * mpp));
}

/** Degrees into [0, 360). */
export function wrapDegrees(deg: number): number {
  const d = ((deg % 360) + 360) % 360;
  return d === 0 ? 0 : d; // never -0
}

/**
 * The pose the BIM view opens at, from the camera the view being left handed over. `from` is that view: a flat 2D
 * map lands at production's default pitch; the globe (and a tilted map) hand over a genuine camera angle.
 * Null when the camera is not a real place (a centre that is not degrees would put the view across the planet).
 */
export function poseFromShared(c: SharedCamera, screenPx: number, from: ViewMode): Pose | null {
  const [lon, lat] = c.center;
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const flat = from !== 'globe' && !(c.pitch >= FLAT_DEG);
  return {
    lat,
    lon,
    dist: zoomToDistance(Number.isFinite(c.zoom) ? c.zoom : 14, lat, screenPx),
    heading: -(Number.isFinite(c.bearing) ? c.bearing : 0) * D2R,
    pitch: flat ? MAP_ARRIVAL_PITCH : Math.max(0.05, (90 - (Number.isFinite(c.pitch) ? c.pitch : 0)) * D2R),
  };
}

/** The lowest BIM pitch handed over (the viewer's own floor above ground, radians). */
const HANDOVER_MIN_PITCH = 0.03;

/** The camera to hand over when leaving BIM: where it looks, how much it shows, its bearing and its tilt. The pitch is
 *  floored where the BIM's own floor is above ground: an underground BIM camera looks UP, and a tilt past 90 degrees
 *  would hand the globe a view from inside the planet. Above ground this changes nothing. */
export function sharedFromPose(p: Pose, screenPx: number): SharedCamera {
  return {
    center: [p.lon, p.lat],
    zoom: distanceToZoom(p.dist, p.lat, screenPx),
    bearing: wrapDegrees(-p.heading * R2D),
    pitch: Math.max(0, 90 - Math.max(HANDOVER_MIN_PITCH, p.pitch) * R2D),
  };
}

/**
 * Where a move of the left map-tools column puts the BIM camera. The parts of the target given are converted as the
 * handshake converts them; the parts left out stay exactly as they are (a zoom keeps the heading and the tilt, the
 * compass keeps the distance). A pitch of 0, Home's top-down map view, lands at the oblique arrival pitch, as arriving
 * from a flat map does: the model camera never gets that close to straight down, so no view it hands over reads as
 * flat. Null when the centre is not a real place.
 */
export function poseForTarget(target: Partial<SharedCamera>, cur: Pose, screenPx: number): Pose | null {
  const now = sharedFromPose(cur, screenPx);
  const p = poseFromShared(
    {
      center: target.center ?? now.center,
      zoom: target.zoom ?? now.zoom,
      bearing: target.bearing ?? now.bearing,
      pitch: target.pitch ?? now.pitch,
    },
    screenPx,
    'map',
  );
  if (!p) return null;
  return {
    lat: target.center ? p.lat : cur.lat,
    lon: target.center ? p.lon : cur.lon,
    dist: target.zoom === undefined ? cur.dist : p.dist,
    heading: target.bearing === undefined ? cur.heading : p.heading,
    pitch: target.pitch === undefined ? cur.pitch : p.pitch,
  };
}

/** Radians into (-pi, pi]: the short way round. */
export function shortestTurn(rad: number): number {
  return Math.atan2(Math.sin(rad), Math.cos(rad));
}

/** Metres between two lat/lon points, on the flat approximation production's movement checks use. */
export function groundMetres(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  return Math.hypot((a.lat - b.lat) * 111320, (a.lon - b.lon) * 111320 * Math.cos(a.lat * D2R));
}
